/**
 * React Query Mutation Hook: Deploy Metadata
 * 
 * Deploys selected metadata items to Salesforce
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { DEPLOY_METADATA_MUTATION } from '@/services/deployment/mutations';
import type {
  DeployMetadataInput,
  DeployMetadataResponse,
  MetadataDeployment,
} from '@/services/deployment/types';

/**
 * Deploy metadata items via GraphQL mutation
 */
async function deployMetadata(input: DeployMetadataInput): Promise<MetadataDeployment> {
  const response = await graphqlRequest<DeployMetadataResponse>(
    DEPLOY_METADATA_MUTATION,
    { input }
  );
  return response.deployMetadata;
}

/**
 * Hook to deploy metadata items
 * 
 * @returns React Query mutation result
 */
export function useDeployMetadata(): UseMutationResult<
  MetadataDeployment,
  Error,
  DeployMetadataInput,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deployMetadata,
    onSuccess: (data, variables) => {
      // Invalidate and refetch relevant queries after successful deployment
      queryClient.invalidateQueries({ queryKey: ['editedMetadataItems'] });
      queryClient.invalidateQueries({ queryKey: ['metadataDeployments'] });
      
      console.log('✅ Deployment initiated:', data);
    },
    onError: (error) => {
      console.error('❌ Deployment failed:', error);
    },
  });
}

